import { Injectable, inject } from "@angular/core";
import { Ticket } from "@app/models/rest/ticket";
import { TicketRestService } from "@app/services/hal/ticket-rest.service";
import { RealTimeStreamingStore } from "@app/services/streaming/real-time-streaming-store";
import { TicketStatus } from "@app/models/enums/ticket-status.enum";
import moment from "moment";
import { TicketUpdateSource } from "@app/models/enums/ticket-update-source.enum";
import { StompRouterService } from "@app/services/webstomp/stomp-router.service";
import { TeamStore } from "@app/services/store/team-store";
import { Team } from "@app/models/rest/team";
import { ResourceStore } from "@app/services/store/resource-store";
import { ResourceModel } from "@app/models/rest/resource";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { HateoasResourceService } from "@lagoshny/ngx-hateoas-client";

@Injectable({ providedIn: "root" })
export class TicketStreamingStore extends RealTimeStreamingStore<Ticket> {
  protected resourceStore = inject(ResourceStore);
  protected teamStore = inject(TeamStore);
  serverErrorHandler = inject(ServiceErrorHandlerService);

  /* eslint-disable @angular-eslint/prefer-inject -- Subclass calling super() with constructor params */
  constructor(
    ticketRestService: TicketRestService,
    stompRouterService: StompRouterService,
    hateoasResourceService: HateoasResourceService
  ) {
    /* eslint-enable @angular-eslint/prefer-inject */
    super(ticketRestService, stompRouterService, hateoasResourceService);
    super.subscribeToMessaging("Ticket");
    stompRouterService.addSubscription("/topic/ticket/unassigned/");
    stompRouterService.addSubscription("/topic/ticket/noteam/");
    this.teamStore.loadAll().then((teamList) => {
      teamList?.resources?.forEach((value) => {
        stompRouterService.addSubscription("/topic/ticket/" + value.name + "/");
      });
    });
  }

  public update(entity: Ticket) {
    entity.lastUpdateSource = TicketUpdateSource.WebApi;
    return super.update(entity);
  }

  public setTicketStatus(
    ticket: Ticket,
    ticketStatus: TicketStatus
  ): Promise<Ticket> {
    return new Promise<Ticket>((resolve, reject) => {
      this.loadSingle(ticket.id).then((planningTicket) => {
        console.log("Setting ticket status");
        console.log(ticket);
        if (ticket.ticketStatus !== ticket.ticketStatus) {
          reject("Ticket status was already changed");
          return;
        }
        planningTicket.ticketStatus = ticketStatus;
        planningTicket.lastUpdateSource = TicketUpdateSource.WebApi;
        const now = moment().startOf("second").parseZone().toDate();
        switch (ticketStatus) {
          case TicketStatus.DESPATCHED:
            planningTicket.despatchedTime = now;
            break;
          case TicketStatus.ENROUTE:
            planningTicket.enrouteTime = now;
            break;
          case TicketStatus.ARRIVED:
            planningTicket.arrivedTime = now;
            break;
          case TicketStatus.STARTED:
            planningTicket.startedTime = now;
            break;
          case TicketStatus.COMPLETE:
            reject("Cannot complete a ticket like this");
        }

        this.update(planningTicket).subscribe(
          (value: Ticket) => {
            resolve(value);
          },
          (error1) => {
            reject(error1);
          }
        );
      });
    });
  }

  findByTimeRangeAndTeams(
    from: Date,
    to: Date,
    teamList: Team[]
  ): Promise<any> {
    console.log(teamList);
    let teamListString = teamList.map((value) => value.id).join(",");
    return this.loadWithSearch(
      "findByTimeRangeAndTeams",
      {
        params: {
          fromDate: moment(from).toISOString(),
          toDate: moment(to).toISOString(),
          teamsList: teamListString,
          projection: "detail"
        }
      },
      false
    );
  }

  public findByResourceAvailability(resourceAvailabilityId: number) {
    return this.loadWithSearchPage(
      "findByResourceAvailability",
      {
        params: {
          resourceAvailabilityId: resourceAvailabilityId,
          projection: "detail"
        }
      },
      true
    );
  }

  forceTicketStart(id: number, startDate: string | Date): Promise<void> {
    console.log("setting forced ticket start: ", id, startDate);
    return this.loadSingle(id).then(
      (value) =>
        new Promise<void>((resolve, reject) => {
          // A null/falsy startDate clears the time lock. Set it explicitly
          // rather than relying on moment(null) producing an invalid date that
          // happens to serialise to null (which also triggers a deprecation
          // warning).
          value.forceStartTime = startDate
            ? moment(startDate).parseZone().toDate()
            : null;
          this.update(value).subscribe({
            next: () => resolve(),
            error: (err) => reject(err)
          });
        })
    );
  }

  lockResourceAsync(ticketId: number, resourceId: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.loadSingle(ticketId)
        .then((ticket) => {
          if (resourceId) {
            this.resourceStore
              .loadSingle(resourceId)
              .then((resource) => {
                ticket.bindRelation("lockToResource", resource).subscribe(
                  () => {
                    resolve(true);
                  },
                  (error) => {
                    reject(error);
                  }
                );
              })
              // Without this the promise never settles if the resource load
              // fails, leaving the Manual Planning dialog stuck on its spinner
              // (onConfirmClick's then/catch never runs). (#1511)
              .catch(reject);
          } else {
            console.log("deleting lockToResource relation");
            ticket.getRelation<ResourceModel>("lockToResource").subscribe(
              (value) => {
                if (value) {
                  console.log("deleting lock");
                  ticket.unbindRelation("lockToResource").subscribe();
                  resolve(true);
                } else {
                  console.log("no action required");
                  resolve(true);
                }
              },
              (error) => {
                reject(error);
              }
            );
          }
        })
        .catch(reject);
    });
  }
}
